import { remove, uploadData } from "aws-amplify/storage";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileImage,
  FaFileAudio,
  FaFileVideo,
  FaFileArchive,
  FaFileAlt,
  FaFileCode,
} from "react-icons/fa";
import { IconType } from "react-icons";

export const FIXED_SCHEDULED_UTC_SLOTS = ["0600", "1400", "2200"] as const;
export type FixedScheduledUtcSlot = (typeof FIXED_SCHEDULED_UTC_SLOTS)[number];
export const DEFAULT_SCHEDULED_UTC_SLOT: FixedScheduledUtcSlot = "0600";

const padDatePart = (n: number) => String(n).padStart(2, "0");

export function formatCurrency(price: number): string {
  return `$${price.toFixed(2)}`;
}

/**
 * Converts a UTC date/time string to a local date/time string.
 * @param utcString - A UTC ISO date string (e.g., "2025-07-28T14:00:00Z")
 * @param type - 'ShowDayLong' | 'ShowDayShort'
 * @returns Local date/time string (e.g., "July 28, 2025, 10:00 AM")
 */
export const convertUtcToLocal = (
  utcString: string | null | undefined,
  type: string | null,
): string => {
  if (!utcString) return "";

  const date = new Date(utcString);
  if (isNaN(date.getTime())) return "";

  const pad = (n: number) => String(n).padStart(2, "0");

  switch (type) {
    /** Weekday + full date and time */
    case "ShowDayLong":
      return date.toLocaleString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

    /** Short weekday + full date and time */
    case "ShowDayShort":
      return date.toLocaleString(undefined, {
        weekday: "short",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

    /** Full date and time (no weekday) */
    case "NoDay":
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

    /** ✅ NEW: Weekday and full date (NO time) */
    case "DateOnlyWithDay":
      return date.toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

    /** ✅ NEW: Full date only (NO weekday, NO time) */
    case "DateOnly":
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

    /** ISO-like local format for inputs */
    case "NoFormat": {
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    default:
      return "";
  }
};

export const convertLocalToUtc = (localDateTime: string | Date): string => {
  const localDate =
    typeof localDateTime === "string" ? new Date(localDateTime) : localDateTime;
  return localDate.toISOString(); // returns UTC time as ISO string
};

export const getLocalDateInputValue = (dateValue: string | Date): string => {
  const date = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
  if (isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = padDatePart(date.getMonth() + 1);
  const day = padDatePart(date.getDate());
  return `${year}-${month}-${day}`;
};

export const getTomorrowLocalDateInputValue = (): string => {
  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return getLocalDateInputValue(tomorrow);
};

export const getUtcDateInputValue = (
  utcString: string | null | undefined,
): string => {
  if (!utcString) return "";

  const date = new Date(utcString);
  if (isNaN(date.getTime())) return "";

  return `${date.getUTCFullYear()}-${padDatePart(date.getUTCMonth() + 1)}-${padDatePart(date.getUTCDate())}`;
};

export const getScheduledUtcSlotFromDate = (
  utcString: string | null | undefined,
): FixedScheduledUtcSlot => {
  if (!utcString) return DEFAULT_SCHEDULED_UTC_SLOT;

  const date = new Date(utcString);
  if (isNaN(date.getTime())) return DEFAULT_SCHEDULED_UTC_SLOT;

  const slot = `${padDatePart(date.getUTCHours())}${padDatePart(date.getUTCMinutes())}`;
  return FIXED_SCHEDULED_UTC_SLOTS.includes(slot as FixedScheduledUtcSlot)
    ? (slot as FixedScheduledUtcSlot)
    : DEFAULT_SCHEDULED_UTC_SLOT;
};

export const combineUtcDateAndSlot = (
  dateValue: string,
  slot: FixedScheduledUtcSlot = DEFAULT_SCHEDULED_UTC_SLOT,
): string => {
  const trimmedDate = dateValue.trim();
  if (!trimmedDate) return "";

  const year = Number(trimmedDate.slice(0, 4));
  const month = Number(trimmedDate.slice(5, 7));
  const day = Number(trimmedDate.slice(8, 10));
  const hours = Number(slot.slice(0, 2));
  const minutes = Number(slot.slice(2, 4));

  if ([year, month, day, hours, minutes].some((value) => Number.isNaN(value))) {
    return "";
  }

  return new Date(
    Date.UTC(year, month - 1, day, hours, minutes, 0, 0),
  ).toISOString();
};

export const getScheduledSlotLocalLabel = (
  slot: FixedScheduledUtcSlot,
  dateValue?: string | null,
): string => {
  const baseDate = dateValue?.trim() || getTomorrowLocalDateInputValue();
  const utcString = combineUtcDateAndSlot(baseDate, slot);
  if (!utcString) return "";

  const date = new Date(utcString);
  if (isNaN(date.getTime())) return "";

  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/**
 * Validates and formats a phone number into strict E.164 format for AWSPhone.
 * Returns the formatted number (e.g., +12025550123) or null if invalid.
 */
export const formatPhoneToE164 = (input: string): string => {
  if (!input) return "";

  // Remove non-digit characters
  const digitsOnly = input.replace(/\D/g, "");

  // If already E.164 format, verify total digit count
  if (/^\+\d{11,15}$/.test(input)) {
    return input;
  }

  // U.S.-style 10-digit number: assume +1
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }

  // International number: allow only if it's 11–15 digits
  if (digitsOnly.length >= 11 && digitsOnly.length <= 15) {
    return `+${digitsOnly}`;
  }
  // Invalid length
  return "";
};

export const validatePhone = (phone: string): boolean => {
  const trimmed = phone.trim();

  // Allow digits, spaces, hyphens, parentheses, optional leading +
  const allowedCharsRegex = /^[+]?[\d\s\-()]+$/;
  if (!allowedCharsRegex.test(trimmed)) return false;

  // Count digits only
  const digitsOnly = trimmed.replace(/\D/g, "");

  // Require a realistic number of digits
  return digitsOnly.length >= 10 && digitsOnly.length <= 15;
};

export const validateEmail = (email: string): boolean => {
  if (!email || email === "") return false;
  const emailRegex =
    /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*.?[a-zA-Z0-9])*.[a-zA-Z](-?[a-zA-Z0-9])+$/;
  return emailRegex.test(email.trim());
};

export function ensureHtmlBody(text: string): string {
  if (!text) return "";
  if (/<[a-z][\s\S]*>/i.test(text)) return text;
  return text
    .trim()
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map(
      (line) =>
        `<p>${line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`,
    )
    .join("");
}

export const stripHtml = (html: string): string => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || "";
};

export const toErrorWithMessage = (
  maybeError: unknown,
): { message: string } => {
  if (
    typeof maybeError === "object" &&
    maybeError !== null &&
    "message" in maybeError &&
    typeof (maybeError as any).message === "string"
  ) {
    return maybeError as { message: string };
  }

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    return new Error(String(maybeError));
  }
};

export const getUserFriendlyErrorMessage = (error: unknown): string => {
  const message = toErrorWithMessage(error).message;

  // Example: handle common AWS Amplify or network error patterns
  if (message.includes("Network Error")) {
    return "Network issue — please check your internet connection.";
  }

  if (message.includes("User is not authorized")) {
    return "You don’t have permission to perform this action.";
  }

  if (message.includes("Validation error")) {
    return "Some required fields are missing or incorrect.";
  }

  if (message.includes("AccessDenied")) {
    return "Access denied. Please check your credentials or permissions.";
  }

  // Default: Capitalize and clean generic error
  return formatDefaultMessage(message);
};

const formatDefaultMessage = (msg: string): string => {
  const firstChar = msg.trim().charAt(0).toUpperCase();
  return firstChar + msg.trim().slice(1);
};

export const getFriendlyFileTypeName = (mimeType: string): string => {
  const mimeMap: Record<string, string> = {
    // Microsoft Office
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "Microsoft Word",
    "application/msword": "Microsoft Word (Legacy)",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      "Microsoft Excel",
    "application/vnd.ms-excel": "Microsoft Excel (Legacy)",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      "Microsoft PowerPoint",
    "application/vnd.ms-powerpoint": "Microsoft PowerPoint (Legacy)",

    // PDF
    "application/pdf": "PDF Document",

    // Text
    "text/plain": "Plain Text",
    "text/csv": "CSV File",

    // Images
    "image/jpeg": "JPEG Image",
    "image/png": "PNG Image",
    "image/gif": "GIF Image",
    "image/svg+xml": "SVG Vector Image",
    "image/webp": "WebP Image",

    // Audio
    "audio/mpeg": "MP3 Audio",
    "audio/wav": "WAV Audio",
    "audio/ogg": "OGG Audio",

    // Video
    "video/mp4": "MP4 Video",
    "video/webm": "WebM Video",
    "video/x-msvideo": "AVI Video",

    // Archives
    "application/zip": "ZIP Archive",
    "application/x-rar-compressed": "RAR Archive",

    // JSON / XML
    "application/json": "JSON File",
    "application/xml": "XML File",
  };
  return mimeMap[mimeType] || mimeType;
};

export const getFileTypeIcon = (mimeType: string): IconType => {
  const type = mimeType?.toLowerCase() ?? "";
  if (type.includes("word")) return FaFileWord;
  if (type.includes("excel") || type.includes("spreadsheet"))
    return FaFileExcel;
  if (type.includes("presentation") || type.includes("powerpoint"))
    return FaFilePowerpoint;
  if (type.includes("pdf")) return FaFilePdf;
  if (type.includes("image")) return FaFileImage;
  if (type.includes("audio")) return FaFileAudio;
  if (type.includes("video")) return FaFileVideo;
  if (type.includes("zip") || type.includes("rar")) return FaFileArchive;
  if (type.includes("json") || type.includes("xml")) return FaFileCode;
  return FaFileAlt;
};

export const uploadFileToS3 = async (
  file: File,
  identityId: string,
): Promise<string> => {
  if (!identityId) {
    logToConsole("log", "User upload identity id invalid", identityId);
    return "";
  }
  const safeFileName = file.name.replace(/[\\/]/g, "_").trim() || file.name;
  const path = `private/${identityId}/${safeFileName}`;
  try {
    const result = await uploadData({
      path: path,
      data: file,
      options: {
        contentType: file.type,
        onProgress: (progress) =>
          logToConsole("log", "Upload progress:", progress),
      },
    }).result;

    logToConsole("log", "File uploaded to S3 with key:", result.path);
    return result.path;
  } catch (error) {
    logToConsole("error", "S3 upload failed:", error);
    throw error;
  }
};

/**
 * Deletes a file from S3 given its S3 key (path).
 * @param s3Key - The full key/path to the file in the S3 bucket.
 * @returns A boolean indicating success.
 */
export type DeleteS3Result = "deleted" | "not_found" | "failed";

export const deleteFileFromS3 = async (
  s3Key: string,
): Promise<DeleteS3Result> => {
  if (!s3Key) {
    logToConsole("error", "Invalid S3 key provided for deletion.");
    return "failed";
  }

  try {
    await remove({ path: s3Key });
    return "deleted";
  } catch (error: any) {
    const message = String(error?.message ?? "");
    const name = String(error?.name ?? "");

    // Treat missing objects as safe to continue
    if (
      name.includes("NoSuchKey") ||
      name.includes("NotFound") ||
      message.includes("NoSuchKey") ||
      message.includes("NotFound") ||
      message.includes("does not exist")
    ) {
      return "not_found";
    }

    logToConsole("error", `Failed to delete file from S3: ${s3Key}`, error);
    return "failed";
  }
};

export function getHomeRoute(isAuthenticated: boolean) {
  return isAuthenticated ? "/Dashboard" : "/";
}

export function capitalizeFirst(str: string, locale = "es"): string {
  if (!str) return str;
  return str[0].toLocaleUpperCase(locale) + str.slice(1);
}

function getAssociatedMessageScheduledDate(item: any): string | null {
  // Items come either as a join row ({ message: { ...scheduledDate } }, the
  // Recipient/Attachment "...WithMessages" query shape) or, in some call sites, as
  // the Message itself - support both rather than assuming one shape.
  return item?.message?.scheduledDate ?? item?.scheduledDate ?? null;
}

export function getSortedAssociatedMessages(source: any) {
  const messages = source?.messages;

  const items = Array.isArray(messages) ? messages : messages?.items;

  if (!Array.isArray(items)) return [];

  return [...items].sort((a, b) => {
    const scheduledDateA = getAssociatedMessageScheduledDate(a);
    const scheduledDateB = getAssociatedMessageScheduledDate(b);
    const da = scheduledDateA ? new Date(scheduledDateA).getTime() : Infinity;
    const db = scheduledDateB ? new Date(scheduledDateB).getTime() : Infinity;
    return da - db;
  });
}

const isDev = process.env.NODE_ENV !== "production";
type LogLevel = "log" | "warn" | "error";

const REDACTED = "[REDACTED]";
const MAX_DEPTH = 4;
const MAX_ARRAY_LENGTH = 20;

const SENSITIVE_KEY_PATTERN =
  /pass(word)?|secret|token|api[_-]?key|authorization|cookie|session|credential|signature|private[_-]?key|access[_-]?token|refresh[_-]?token/i;

function sanitizeForLog(
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
): unknown {
  if (value == null) return value;

  const valueType = typeof value;

  if (
    valueType === "string" ||
    valueType === "number" ||
    valueType === "boolean" ||
    valueType === "bigint"
  ) {
    return value;
  }

  if (valueType === "function") {
    return `[Function ${(value as Function).name || "anonymous"}]`;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      ...(isDev && value.stack ? { stack: value.stack } : {}),
    };
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof RegExp) {
    return value.toString();
  }

  if (depth >= MAX_DEPTH) {
    if (Array.isArray(value)) return `[Array(${value.length})]`;
    return "[Object]";
  }

  if (typeof value === "object") {
    if (seen.has(value as object)) return "[Circular]";
    seen.add(value as object);

    if (Array.isArray(value)) {
      return value
        .slice(0, MAX_ARRAY_LENGTH)
        .map((item) => sanitizeForLog(item, depth + 1, seen));
    }

    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};

    for (const [key, val] of Object.entries(obj)) {
      if (SENSITIVE_KEY_PATTERN.test(key)) {
        out[key] = REDACTED;
      } else {
        out[key] = sanitizeForLog(val, depth + 1, seen);
      }
    }

    return out;
  }

  try {
    return String(value);
  } catch {
    return "[Unserializable]";
  }
}

export function logToConsole(level: LogLevel, ...args: unknown[]) {
  if (!isDev && level !== "error") return;

  const safeArgs = args.map((arg) => sanitizeForLog(arg));

  switch (level) {
    case "log":
      if (isDev) console.log(...safeArgs);
      break;
    case "warn":
      if (isDev) console.warn(...safeArgs);
      break;
    case "error":
      console.error(...safeArgs);
      break;
  }
}
